import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const STORAGE_KEY = 'shop_cart';

// 每一筆購物車品項以 product_id + variant_id 為唯一鍵（不同變體＝不同行）
const lineKey = (productId, variantId) => `${productId}:${variantId || ''}`;

// 相容：舊版本以 odoo_product_id 命名主鍵
const itemProductId = (it) => (it.product_id != null ? it.product_id : it.odoo_product_id);
// 客製品項每筆都是獨立設計（product_id/variant_id 都相同），必須用 custom_uid 當唯一鍵，
// 否則兩筆客製會被視為同一行而合併（只顯示一筆）。
export const keyOf = (it) => (it.is_custom && it.custom_uid)
  ? `c:${it.custom_uid}`
  : lineKey(itemProductId(it), it.variant_id);

// 穩定的行內唯一碼：僅供畫面 render key 用（與 keyOf 的「合併身分」脫鉤）。
// 換規格會改變 variant_id → keyOf 會變，但 uid 不變，卡片就不會被 React 卸載重掛（不抖）。
const newUid = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const loadCart = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(arr)) return [];
    // 舊資料補上 uid，避免既有購物車換規格時仍會抖
    return arr.map((it) => (it && it.uid ? it : { ...it, uid: newUid() }));
  } catch {
    return [];
  }
};

// 客製手鏈：與後端一致的價格計算
//   base = {elastic,adjustable,magnetic}[chain_style]（缺值 fallback adjustable）
//   +double_add（雙圈）；+oversize_add（手圍 > oversize_threshold）
export function computeCustomPrice(detail, pricing) {
  if (!pricing) return 0;
  const base =
    { elastic: pricing.elastic, adjustable: pricing.adjustable, magnetic: pricing.magnetic }[detail?.chain_style];
  let item = base != null ? base : (pricing.adjustable || 0);
  if (detail?.circle_design === 'double') item += (pricing.double_add || 0);
  const w = parseFloat(detail?.wrist_size);
  if (!Number.isNaN(w) && w > (pricing.oversize_threshold || 17)) item += (pricing.oversize_add || 0);
  return item;
}

// 客製品項規格標籤：<鏈形｜圈數｜手圍>
export function customSpecLabel(detail) {
  const chainMap = { elastic: '彈性繩', adjustable: '調節鍊', magnetic: '磁扣款' };
  const circleMap = { single: '單圈', double: '雙圈' };
  const chain = chainMap[detail?.chain_style] || '調節鍊';
  const circle = circleMap[detail?.circle_design] || '單圈';
  const wrist = detail?.wrist_size != null ? `${detail.wrist_size}cm` : '未定';
  return `${chain}｜${circle}｜${wrist}`;
}

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadCart);

  // 任何變更都寫回 localStorage
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch {}
  }, [items]);

  /**
   * 加入購物車。標準品項形狀：
   *   { product_id, variant_id, name, spec_label, price, qty, image_url }
   * variant_id 為 null、spec_label 為 '' 表示無變體商品。
   * 以 product_id:variant_id 為鍵，不同變體＝不同行，重複則累加數量。
   * （另存 odoo_product_id 別名，向下相容既有頁面。）
   */
  const add = useCallback((item, qty = 1) => {
    const productId = item.product_id != null ? item.product_id : item.odoo_product_id;
    if (productId == null) return;
    const addQty = qty > 0 ? qty : 1;

    // 客製品項：每筆都是獨立設計，配一個唯一 custom_uid，永遠新增一行（不與其他客製合併）
    if (item.is_custom) {
      const uid = item.custom_uid || `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      setItems((prev) => ([
        ...prev,
        {
          product_id: productId,
          odoo_product_id: productId,
          variant_id: item.variant_id ?? null,
          name: item.name,
          spec_label: item.spec_label || '',
          price: item.price || 0,
          qty: addQty,
          image_url: item.image_url || null,
          is_custom: true,
          custom_uid: uid,
          uid: newUid(),
          custom_detail: item.custom_detail || {},
        },
      ]));
      return;
    }

    const variantId = item.variant_id ?? null;
    const key = lineKey(productId, variantId);

    setItems((prev) => {
      const idx = prev.findIndex((it) => keyOf(it) === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: (next[idx].qty || 1) + addQty };
        return next;
      }
      return [
        ...prev,
        {
          product_id: productId,
          odoo_product_id: productId, // 相容別名
          variant_id: variantId,
          uid: newUid(),
          name: item.name,
          spec_label: item.spec_label || '',
          price: item.price || 0,
          qty: addQty,
          image_url: item.image_url || null,
          // 客製品項：帶 is_custom 旗標與完整設計資料
          ...(item.is_custom ? { is_custom: true, custom_detail: item.custom_detail || {} } : {}),
        },
      ];
    });
  }, []);

  // remove(productId) 或 remove(productId, variantId)
  const remove = useCallback((productId, variantId = null) => {
    const key = lineKey(productId, variantId);
    setItems((prev) => prev.filter((it) => keyOf(it) !== key));
  }, []);

  // 以行鍵移除（客製品項用；同 product_id 多筆時才能精準刪對的那筆）
  const removeByKey = useCallback((key) => {
    setItems((prev) => prev.filter((it) => keyOf(it) !== key));
  }, []);

  // updateQty(productId, qty) 或 updateQty(productId, variantId, qty)
  const updateQty = useCallback((productId, arg2, arg3) => {
    const hasVariant = arg3 !== undefined;
    const variantId = hasVariant ? arg2 : null;
    const qty = hasVariant ? arg3 : arg2;
    const key = lineKey(productId, variantId);
    const n = Math.max(1, parseInt(qty, 10) || 1);
    setItems((prev) =>
      prev.map((it) => (keyOf(it) === key ? { ...it, qty: n } : it))
    );
  }, []);

  // updateCustom(key, patchDetail, pricing)
  //   更新客製品項的 custom_detail，並依 pricing 重算 price + spec_label。
  //   key 為 lineKey（`${product_id}:${variant_id||''}`）；pricing 來自 /api/settings/pricing。
  const updateCustom = useCallback((key, patchDetail, pricing) => {
    setItems((prev) =>
      prev.map((it) => {
        if (keyOf(it) !== key || !it.is_custom) return it;
        const nextDetail = { ...(it.custom_detail || {}), ...(patchDetail || {}) };
        return {
          ...it,
          custom_detail: nextDetail,
          spec_label: customSpecLabel(nextDetail),
          price: pricing ? computeCustomPrice(nextDetail, pricing) : it.price,
        };
      })
    );
  }, []);

  // 變更「設計款」品項的變體規格。newVariant: { variant_id, spec_label, price }
  // 若購物車已有相同 product+新變體 的行，則併行累加數量。
  const updateVariant = useCallback((productId, oldVariantId, newVariant) => {
    const oldKey = lineKey(productId, oldVariantId);
    const newVid = newVariant.variant_id ?? null;
    const newKey = lineKey(productId, newVid);
    if (oldKey === newKey) return;
    setItems((prev) => {
      const idx = prev.findIndex((it) => keyOf(it) === oldKey);
      if (idx < 0) return prev;
      const moving = prev[idx];
      const next = [...prev];
      const existingIdx = prev.findIndex((it) => keyOf(it) === newKey);
      if (existingIdx >= 0 && existingIdx !== idx) {
        next[existingIdx] = { ...next[existingIdx], qty: (next[existingIdx].qty || 1) + (moving.qty || 1) };
        next.splice(idx, 1);
      } else {
        next[idx] = {
          ...moving,
          variant_id: newVid,
          spec_label: newVariant.spec_label ?? moving.spec_label,
          price: newVariant.price != null ? newVariant.price : moving.price,
        };
      }
      return next;
    });
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const subtotal = useMemo(
    () => items.reduce((sum, it) => sum + (it.price || 0) * (it.qty || 1), 0),
    [items]
  );

  const count = useMemo(
    () => items.reduce((sum, it) => sum + (it.qty || 1), 0),
    [items]
  );

  const value = useMemo(
    () => ({ items, add, remove, removeByKey, updateQty, updateCustom, updateVariant, clear, subtotal, count }),
    [items, add, remove, removeByKey, updateQty, updateCustom, updateVariant, clear, subtotal, count]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error('useCart 必須在 <CartProvider> 內使用');
  }
  return ctx;
}

export default CartContext;
